import axios from "axios";
import Cookies from "js-cookie";

// FIX_RULES 10.1 (NEW-83): one API client. Every call below goes through request(): one server address, one bearer
// token, the one axios that carries the session interceptor (401 -> Session Expired, 403 / 5xx -> message,
// apis/sessionInterceptor.js) and the double-submit guard (apis/submitGuard.js). The exported helpers only shape the
// body the endpoint expects (form data, nested form data, JSON, url-encoded).

const BASE_URL = String(process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "");
// UI-09: one slash between the server and the path, whichever way the caller wrote it (paths came out as "//x")
const url = (endpoint) => `${BASE_URL}/${String(endpoint).replace(/^\/+/, "")}`;

// AUTH-32: send a bearer token only when there is one (never "Bearer undefined")
const authHeader = () => {
  const token = Cookies.get("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const request = (method, endpoint, data, contentType, { auth = true, headers = {} } = {}) =>
  axios.request({
    method,
    url: url(endpoint),
    data,
    headers: {
      ...(auth ? authHeader() : {}),
      ...(contentType ? { "Content-Type": contentType } : {}),
      ...headers,
    },
  });

// body shapes ---------------------------------------------------------------------------------------------------------

// flat form data; UI-06: an empty value is left out (appending null / undefined sent the text "null" / "undefined")
const flatForm = (data) => {
  const formData = new FormData();
  Object.keys(data || {}).forEach((key) => {
    if (data[key] === null || data[key] === undefined) return;
    formData.append(key, data[key]);
  });
  return formData;
};

// nested objects and arrays as key[sub][0] fields
const nestedForm = (data) => {
  const formData = new FormData();
  const append = (value, key) => {
    if (value && typeof value === "object" && !Array.isArray(value) && !(value instanceof Blob)) {
      Object.keys(value).forEach((k) => append(value[k], key ? `${key}[${k}]` : k));
    } else if (Array.isArray(value)) {
      value.forEach((item, i) => append(item, `${key}[${i}]`));
    } else {
      formData.append(key, value);
    }
  };
  append(data, "");
  return formData;
};

const urlEncoded = (data) => {
  const params = new URLSearchParams();
  Object.keys(data || {}).forEach((key) => params.append(key, data[key]));
  return params;
};

const MULTIPART = "multipart/form-data";
const FORM = "application/x-www-form-urlencoded";
const JSON_TYPE = "application/json";

// public calls ---------------------------------------------------------------------------------------------------------

export const checkUser = (phone) => request("post", "checkuser", flatForm({ phone }), MULTIPART, { auth: false });

export const postReqLogin = (data, endPoint) => request("post", endPoint, flatForm(data), MULTIPART, { auth: false });

// logout sends the token it is ending, even when the cookie is already gone
export const postReqLogout = (endPoint, token) =>
  request("post", endPoint, {}, JSON_TYPE, { auth: false, headers: { Authorization: `Bearer ${token}` } });

// reads ------------------------------------------------------------------------------------------------------------------

// the response body
export const getReq = async (endpoint) => (await request("get", endpoint)).data;

// a POST read (search) with no body: the response body
export const searchReq = async (endpoint) => (await request("post", endpoint, null)).data;

// writes -----------------------------------------------------------------------------------------------------------------

export const postReq = (data, endPoint) => request("post", endPoint, flatForm(data), MULTIPART);

// a FormData the page built itself (file uploads)
export const postReqfileupload = (formData, endPoint) => request("post", endPoint, formData, MULTIPART);

export const nestedPostReq = (data, endPoint) => request("post", endPoint, nestedForm(data), MULTIPART);

export const jsonpostReq = (data, endPoint) => request("post", endPoint, data, JSON_TYPE);

export const postReqSecure = (data, endPoint) => request("post", endPoint, urlEncoded(data), FORM);

export const putReq = (data, endPoint) => request("put", endPoint, urlEncoded(data), FORM);

export const deleteReq = (endPoint) => request("delete", endPoint, undefined, JSON_TYPE);

// payment form: its fields plus the screenshot file
export const postReqwithFile = (data, paymentScreenshot, endPoint) => {
  const formData = flatForm(Object.fromEntries(Object.entries(data || {}).filter(([k]) => k !== "payment_screenshot")));
  if (paymentScreenshot) {
    formData.append("payment_screenshot", paymentScreenshot, paymentScreenshot.name);
  }
  return request("post", endPoint, formData, MULTIPART);
};

// warranty claim: products[i][field], with each product's issue photos as products[i][issue_description][]
export const postReqwithIssueDesc = (data, endPoint) => {
  const formData = new FormData();
  Object.keys(data).forEach((key) => {
    if (key === "products") {
      data.products.forEach((product, index) => {
        Object.keys(product).forEach((productKey) => {
          if (productKey === "issue_description") {
            product[productKey].forEach((file) => formData.append(`products[${index}][${productKey}][]`, file));
          } else {
            formData.append(`products[${index}][${productKey}]`, product[productKey]);
          }
        });
      });
    } else {
      formData.append(key, data[key]);
    }
  });
  return request("post", endPoint, formData, MULTIPART);
};

// product / part form: image files plus the bill-of-materials lines as key[i][field]
// SGEW 19 Oct (field audit): only new image files go under product_images / part_images, numbered from 0 (the names of
// the stored images the form still shows go as kept_images - the server read names here as data); a line without a
// record is not sent and a group left with no line is sent empty (key=""), so an edit clears it (otherwise the stored
// lines stayed); an empty value is left out (undefined / null arrived as the text "undefined" / "null")
const IMAGE_KEYS = ["product_images", "part_images"];
const LINE_KEYS = ["item_details", "subitems", "parts"];
const hasValue = (v) => v !== null && v !== undefined;
export const rawPostReq = (data, endPoint) => {
  const formData = new FormData();
  Object.keys(data || {}).forEach((key) => {
    const value = data[key];
    if (IMAGE_KEYS.includes(key)) {
      (Array.isArray(value) ? value : [])
        .filter((file) => file instanceof File)
        .forEach((file, index) => formData.append(`${key}[${index}]`, file));
    } else if (LINE_KEYS.includes(key) && Array.isArray(value)) {
      const rows = value.filter((row) => row && hasValue(row.id) && String(row.id).trim() !== "");
      if (!rows.length) formData.append(key, "");
      rows.forEach((row, index) =>
        // a line's empty value is sent blank: the server checks each line field and names the one missing; SGEW 21 Oct: a
        // weight or cost left blank (or NaN) is 0 - a record without a default weight or cost has none to send
        Object.keys(row).forEach((subKey) => {
          let v = hasValue(row[subKey]) ? row[subKey] : "";
          if (["weightPerUnit", "sgew_price"].includes(subKey) && !Number.isFinite(parseFloat(v))) v = 0;
          formData.append(`${key}[${index}][${subKey}]`, v);
        })
      );
    } else if (hasValue(value)) {
      formData.append(key, value);
    }
  });
  return request("post", endPoint, formData, MULTIPART);
};
